import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

interface UseInfiniteListOptions<T> {
  /**
   * Full query key driving refetches. Must be memoized by the caller.
   * `itemsToLoad` is appended internally — do not include it here.
   */
  queryKey: unknown[];
  /**
   * Fetcher invoked with the current item window. Should return the first
   * `limit` items plus a `hasMore` flag.
   */
  queryFn: (limit: number) => Promise<{ items: T[]; hasMore: boolean }>;
  enabled?: boolean;
  initialItemsPerPage: number;
  autoRefreshMs?: number;
  /** When true, suppresses the auto-refresh poll without disabling the query. */
  paused?: boolean;
  /**
   * Reference identity that triggers a window reset (snap items back to
   * `initialItemsPerPage`). Typically the filters object. Reference must be
   * stable across renders that should NOT reset (e.g. selection changes).
   * If omitted, only `initialItemsPerPage` triggers a reset.
   */
  resetKey?: unknown;
}

interface UseInfiniteListResult<T> {
  items: T[];
  hasMore: boolean;
  loading: boolean;
  refreshing: boolean;
  loadMore: () => void;
  lastFetched: Date | null;
  refetch: () => void;
}

export function useInfiniteList<T>({
  queryKey,
  queryFn,
  enabled,
  initialItemsPerPage,
  autoRefreshMs,
  paused,
  resetKey,
}: UseInfiniteListOptions<T>): UseInfiniteListResult<T> {
  const queryClient = useQueryClient();
  const [itemsToLoad, setItemsToLoad] = useState(initialItemsPerPage);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);

  // Derive the reset synchronously during render so the next useQuery call
  // sees the reset itemsToLoad in the same commit. An effect-based reset
  // would let one stale-window query fire (e.g. new filters with the
  // previous 300-item limit) before snapping back. See React docs:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setItemsToLoad(initialItemsPerPage);
  }

  const fullQueryKey = useMemo(() => [...queryKey, itemsToLoad], [queryKey, itemsToLoad]);

  const { data, isLoading, isPlaceholderData, dataUpdatedAt } = useQuery({
    queryKey: fullQueryKey,
    queryFn: () => queryFn(itemsToLoad),
    enabled,
    refetchInterval: paused ? false : autoRefreshMs,
    placeholderData: keepPreviousData,
  });

  const loadMore = useCallback(() => {
    if (!data?.hasMore) return;
    setItemsToLoad((prev) => prev + initialItemsPerPage);
  }, [data?.hasMore, initialItemsPerPage]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: fullQueryKey });
  }, [queryClient, fullQueryKey]);

  return {
    items: data?.items ?? [],
    hasMore: data?.hasMore ?? false,
    // Only true on the initial cold-start fetch (no resolved data, no
    // placeholder). Stays false during bumps and auto-refresh polls thanks
    // to placeholderData — InfinityScroll's enabled prop therefore doesn't
    // toggle on every poll, which would otherwise re-fire onLoadMore via
    // its in-view sentinel re-trigger effect (runaway pagination).
    loading: isLoading,
    // True while displayed items are a held-over placeholder from a previous
    // query key (filter change or window bump) — lets consumers dim stale rows
    // until the refetch lands, mirroring the isPlaceholderData chart pattern.
    refreshing: isPlaceholderData,
    loadMore,
    lastFetched: dataUpdatedAt ? new Date(dataUpdatedAt) : null,
    refetch,
  };
}
